import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';

import { authGet, authPost, authPatch, authDelete } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';

export default function ClassesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    classification: '',
    details: '',
    calendar_color: '#1976D2'
  });

  // 🔥 Load classes
  const loadClasses = async () => {
    try {
      const data = await authGet('/api/classes');
      setRows(data.classes || []);
    } catch (err) {
      console.error('Load classes error:', err);
    }
  };

  useEffect(() => {
    loadClasses();
  }, []);

  // 🔥 Save (Create or Update)
  const handleSave = async () => {
    try {
      if (editing) {
        await authPatch(`/api/classes/${editing.id}`, form);
      } else {
        await authPost('/api/classes', form);
      }

      setOpen(false);
      setEditing(null);
      setForm({
        code: '',
        name: '',
        description: '',
        classification: '',
        details: '',
        calendar_color: '#1976D2'
      });

      loadClasses();
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  // 🔥 Edit
  const handleEdit = (row) => {
    setEditing(row);
    setForm(row);
    setOpen(true);
  };

  // 🔥 Delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class?')) return;

    try {
      await authDelete(`/api/classes/${id}`);
      loadClasses();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // 🔥 Columns
  const columns = [
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 2 },
    { field: 'description', headerName: 'Description', flex: 3 },
    { field: 'classification', headerName: 'Classification', flex: 1 },
    {
      field: 'calendar_color',
      headerName: 'Calendar Color',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} height="100%">
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: params.row.calendar_color || '#1976D2', border: '1px solid', borderColor: 'divider' }} />
          <Typography variant="body2">{params.row.calendar_color || '#1976D2'}</Typography>
        </Box>
      )
    },
    { field: 'details', headerName: 'Details', flex: 2 },

    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={1}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>

          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => handleDelete(params.row.id)}
          >
            DELETE
          </Button>
        </Box>
      ),
    }
  ];

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    setFieldVisible,
    showSimpleView,
    showFullView,
    resetView
  } = useSavedColumnView('classes', columns, ['code', 'name', 'classification', 'calendar_color']);

  return (
    <Box p={3}>
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h3">Classes</Typography>
          <Typography color="text.secondary">
            Manage Alamo Center classes stored in the database.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditing(null);
            setForm({
              code: '',
              name: '',
              description: '',
              classification: '',
              details: ''
            });
            setOpen(true);
          }}
        >
          Create Class
        </Button>
      </Stack>

      {/* TABLE */}
      <Box height={600}>
        <SavedColumnViewControls
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          setFieldVisible={setFieldVisible}
          showSimpleView={showSimpleView}
          showFullView={showFullView}
          resetView={resetView}
        />
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          rowHeight={60}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      </Box>

      {/* MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? 'Edit Class' : 'Create Class'}
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Classification"
            value={form.classification}
            onChange={(e) =>
              setForm({ ...form, classification: e.target.value })
            }
          />

          <TextField
            fullWidth
            margin="normal"
            label="Details"
            value={form.details}
            onChange={(e) => setForm({ ...form, details: e.target.value })}
          />

          <Stack direction="row" spacing={2} alignItems="center" mt={2}>
            <TextField
              label="Calendar Color"
              type="color"
              value={form.calendar_color || '#1976D2'}
              onChange={(e) => setForm({ ...form, calendar_color: e.target.value.toUpperCase() })}
              sx={{ width: 120 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Calendar Color Hex"
              value={form.calendar_color || '#1976D2'}
              onChange={(e) => setForm({ ...form, calendar_color: e.target.value })}
              helperText="Used for this class/program type on calendar views."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}